<app-page-header
  title="dash.title"
  [subtitle]="'dash.subtitle' | t" />

<!-- ── Chargement / erreur ───────────────────────────────────────────────── -->
@if (loading() || error()) {
  <app-data-state
    [loading]="loading()"
    [error]="error()"
    [loadingMessage]="'dash.loading' | t"
    (retry)="load()" />
}

<!-- ── Pas encore opérateur (404) ────────────────────────────────────────── -->
@else if (notAnOperator()) {
  <div
    class="mt-6 rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <div
      class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 8v8m4-4H8"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
      </svg>
    </div>
    <h2 class="mt-4 text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'dash.notOperatorTitle' | t }}</h2>
    <p class="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">{{ 'dash.notOperatorBody' | t }}</p>
    <a
      routerLink="/apply"
      class="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600"
    >{{ 'dash.apply' | t }}</a>
  </div>
}

<!-- ── Tableau de bord ───────────────────────────────────────────────────── -->
@else if (data(); as d) {
  <!-- En-tête identité opérateur -->
  <div
    class="mt-6 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs sm:flex-row sm:items-center sm:justify-between dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <div class="min-w-0">
      <h2 class="truncate text-lg font-semibold text-gray-800 dark:text-white/90">
        {{ d.businessName || 'Opérateur #' + d.operatorId }}
      </h2>
      <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
        @if (d.zoneCode) {
          Zone <span class="font-medium text-gray-700 dark:text-gray-300">{{ d.zoneCode }}</span>
        } @else {
          Zone non définie
        }
      </p>
    </div>
    <span
      class="inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
      [class.bg-brand-50]="statusMeta[d.operatorStatus].palette === 'brand'"
      [class.text-brand-700]="statusMeta[d.operatorStatus].palette === 'brand'"
      [class.dark:bg-brand-500/15]="statusMeta[d.operatorStatus].palette === 'brand'"
      [class.dark:text-brand-400]="statusMeta[d.operatorStatus].palette === 'brand'"
      [class.bg-warning-50]="statusMeta[d.operatorStatus].palette === 'warning'"
      [class.text-warning-700]="statusMeta[d.operatorStatus].palette === 'warning'"
      [class.dark:bg-warning-500/15]="statusMeta[d.operatorStatus].palette === 'warning'"
      [class.dark:text-warning-400]="statusMeta[d.operatorStatus].palette === 'warning'"
      [class.bg-error-50]="statusMeta[d.operatorStatus].palette === 'error'"
      [class.text-error-700]="statusMeta[d.operatorStatus].palette === 'error'"
      [class.dark:bg-error-500/15]="statusMeta[d.operatorStatus].palette === 'error'"
      [class.dark:text-error-400]="statusMeta[d.operatorStatus].palette === 'error'"
      [class.bg-gray-100]="statusMeta[d.operatorStatus].palette === 'gray'"
      [class.text-gray-700]="statusMeta[d.operatorStatus].palette === 'gray'"
      [class.dark:bg-gray-800]="statusMeta[d.operatorStatus].palette === 'gray'"
      [class.dark:text-gray-300]="statusMeta[d.operatorStatus].palette === 'gray'"
    >
      <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
      {{ statusMeta[d.operatorStatus].label | t }}
    </span>
  </div>

  <!-- Bandeaux d'alerte selon le statut -->
  @if (isExhausted()) {
    <div
      class="mt-4 flex flex-col gap-3 rounded-2xl border border-error-200 bg-error-50 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-error-500/30 dark:bg-error-500/10"
    >
      <div class="flex items-start gap-3">
        <svg class="mt-0.5 shrink-0 text-error-500" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v4m0 4h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <div>
          <p class="text-sm font-semibold text-error-700 dark:text-error-400">{{ 'dash.exhaustedTitle' | t }}</p>
          <p class="mt-0.5 text-sm text-error-600 dark:text-error-400/90">{{ 'dash.exhaustedBody' | t }}</p>
        </div>
      </div>
      <a
        routerLink="/wallet"
        class="inline-flex w-fit shrink-0 items-center justify-center rounded-lg bg-error-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-error-600"
      >{{ 'dash.topUpCredit' | t }}</a>
    </div>
  } @else if (isPending()) {
    <div
      class="mt-4 flex items-start gap-3 rounded-2xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/30 dark:bg-warning-500/10"
    >
      <svg class="mt-0.5 shrink-0 text-warning-500" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
        <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <div>
        <p class="text-sm font-semibold text-warning-700 dark:text-orange-400">{{ 'apply.pendingTitle' | t }}</p>
        <p class="mt-0.5 text-sm text-warning-600 dark:text-orange-300/90">{{ 'dash.pendingBody' | t }}</p>
      </div>
    </div>
  } @else if (isSuspended()) {
    <div
      class="mt-4 flex items-start gap-3 rounded-2xl border border-gray-300 bg-gray-100 p-4 dark:border-gray-700 dark:bg-white/[0.04]"
    >
      <svg class="mt-0.5 shrink-0 text-gray-500" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
        <path d="M8 12h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      </svg>
      <div>
        <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ 'dash.suspendedTitle' | t }}</p>
        <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{{ 'dash.suspendedBody' | t }}</p>
      </div>
    </div>
  }

  <!-- KPIs financiers -->
  <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
    <!-- CREDIT -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.creditReserve' | t }}</span>
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" stroke-width="1.8" />
            <path d="M3 10h18M7 15h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </span>
      </div>
      <p class="mt-3 text-2xl font-bold text-gray-800 dark:text-white/90">
        {{ d.creditBalance | number: '1.0-0' }}
        <span class="text-sm font-normal text-gray-400">XOF</span>
      </p>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">{{ 'dash.creditHint' | t }}</p>
    </div>

    <!-- BALANCE -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.balanceRevenue' | t }}</span>
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 18V9m5 9V5m5 13v-6m5 6V8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </span>
      </div>
      <p class="mt-3 text-2xl font-bold text-gray-800 dark:text-white/90">
        {{ d.balanceBalance | number: '1.0-0' }}
        <span class="text-sm font-normal text-gray-400">XOF</span>
      </p>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">{{ 'dash.balanceHint' | t }}</p>
    </div>

    <!-- Disponible au retrait -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.withdrawable' | t }}</span>
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-light-50 text-blue-light-500 dark:bg-blue-light-500/15 dark:text-blue-light-500">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>
      <p class="mt-3 text-2xl font-bold text-gray-800 dark:text-white/90">
        {{ d.withdrawableBalance | number: '1.0-0' }}
        <span class="text-sm font-normal text-gray-400">XOF</span>
      </p>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">{{ 'dash.withdrawableHint' | t }}</p>
    </div>
  </div>

  <!-- KPIs opérationnels -->
  <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
    <!-- Prestataires actifs (sur plafond licence : null = illimité → ∞) -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.activeProviders' | t }}</span>
      <p class="mt-2 text-2xl font-bold text-gray-800 dark:text-white/90">
        {{ d.activePrestataireCount }}
        <span class="text-base font-normal text-gray-400">
          / {{ d.maxPrestataires == null ? '∞' : d.maxPrestataires }}
        </span>
      </p>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
        {{ (d.maxPrestataires == null ? 'dash.capUnlimited' : 'dash.capLicense') | t }}
      </p>
    </div>

    <!-- Commissions du mois -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.monthCommissions' | t }}</span>
      <p class="mt-2 text-2xl font-bold text-gray-800 dark:text-white/90">
        {{ d.commissionsThisMonth | number: '1.0-0' }}
        <span class="text-sm font-normal text-gray-400">XOF</span>
      </p>
    </div>

    <!-- Prestataires en dette -->
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'dash.providersInDebt' | t }}</span>
      <p
        class="mt-2 text-2xl font-bold"
        [class.text-error-600]="d.prestatairesInDebtCount > 0"
        [class.dark:text-error-500]="d.prestatairesInDebtCount > 0"
        [class.text-gray-800]="d.prestatairesInDebtCount === 0"
        [class.dark:text-white/90]="d.prestatairesInDebtCount === 0"
      >
        {{ d.prestatairesInDebtCount }}
      </p>
    </div>
  </div>

  <!-- Analytics (règlements, slice 0) -->
  <app-operator-analytics />

  <!-- Analytics locations (funnel + réservations dans le temps) -->
  <app-rentals-analytics />

  <!-- Analytics parc (répartition par statut + acquisition) -->
  <app-vehicles-analytics />

  <!-- Analytics trésorerie (flux entrées/sorties/net) -->
  <app-wallet-analytics />
}
